import React, { useState, useContext, useMemo, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import PartSidebar from "./PartSidebar";
import PartsCardView from "./PartsCardView";
import PartsListingView from "./PartsListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import usePagedList, { sortToOrderBy } from "../../hooks/usePagedList";
import Paginator from "../../components/Paginator";
import { deactivateData, restoreData } from "../../utils/softDelete";
import "../../starek.css";

const UI_STRINGS = {
    PAGE_TITLE: "Parts",
    SEARCH_PLACEHOLDER: "Search parts…",
    VIEW_MODE_CARD: "Card View",
    VIEW_MODE_LIST: "List View",
    ENTITY_SINGULAR: "Part",
    ENTITY_PLURAL: "Parts",
    DELETE_TITLE_SINGLE: (name) => `Delete Part ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Parts?`,
    DELETE_MESSAGE_SINGLE: (name) => `You are about to delete part "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (count) => `You are about to delete ${count} part(s).

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total parts selected: ${count}`,
    DEACTIVATE_TITLE_SINGLE: (name) => `Deactivate Part ${name}?`,
    DEACTIVATE_TITLE_BULK: (count) => `Deactivate ${count} Parts?`,
    DEACTIVATE_WARNING: `Parts are referenced by Job Schedules and production history.

Deactivating hides this part from new selections. Anything already using it keeps working and is not changed.`,
    DEACTIVATE_CONFIRM: "Deactivate",
    DEACTIVATE_CANCEL: "Cancel",
    DEACTIVATED: (name) => `${name} is deactivated`,
    RESTORED: (name) => `${name} is restored`,
    ERROR_NO_USER: "No current user in context",
    NOT_FOUND: "Part not found",
};

const Parts = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routePartId = parseRouteEntityId(routeId);
    const [searchParams, setSearchParams] = useSearchParams();

    const { can } = usePermissions();
    const canWrite   = can("part", "WRITE");
    const canDestroy = can("part", "DESTROY");

    // Everything the cards, the table and the sidebar read off a part.
    const PART_FIELDS = `
            id
            name
            description
            is_active
            deleted_at
            created_at
            updated_at
            image_asset {
                url
            }
            part_stages {
                id
                operation_id
                sequence_no
                standard_duration
                incremental_weight
                decremental_weight
                absolute_weight
                allowed_devices {
                    device_id
                    position
                }
                operation {
                    id
                    name
                    is_productive
                }
            }
            created_by {
                id
                name
            }
            updated_by {
                id
                name
            }
    `;

    // One page of parts plus the total, for server-side pagination (usePagedList).
    const GET_PARTS_LIST_FOR_PLANT = gql`
        query GetParts(
            $where: PartWhereInput!
            $orderBy: [PartOrderByWithRelationInput!]
            $take: Int
            $skip: Int
        ) {
            parts(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
                ${PART_FIELDS}
            }
            aggregatePart(where: $where) {
                _count {
                    _all
                }
            }
        }
    `;

    // A deep link (/parts/:id) may point at a part that is not on the current page.
    // findFirst goes through the soft-delete filter, so an archived part is not found.
    const GET_PART_BY_ID = gql`
        query GetPartById($where: PartWhereInput!) {
            findFirstPart(where: $where) {
                ${PART_FIELDS}
            }
        }
    `;

    const UPDATE_MANY_PART_MUTATION = gql`
        mutation UpdateManyPart($data: PartUpdateManyMutationInput!, $where: PartWhereInput) {
            updateManyPart(data: $data, where: $where) {
                count
            }
        }
    `;

    const DELETE_ONE_PART_MUTATION = gql`
        mutation DeleteOnePart($where: PartWhereUniqueInput!) {
            deleteOnePart(where: $where) {
                id
                deleted_at
            }
        }
    `;

    const DELETE_MANY_PARTS_MUTATION = gql`
        mutation DeleteManyParts($ids: [Int!]!) {
            deleteManyPart(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const CREATE_ONE_PART_MUTATION = gql`
        mutation CreateOnePart($data: PartCreateInput!) {
            createOnePart(data: $data) {
                id
                name
                description
            }
        }
    `;

    const UPDATE_ONE_PART_MUTATION = gql`
        mutation UpdateOnePart(
            $data: PartUpdateInput!
            $where: PartWhereUniqueInput!
        ) {
            updateOnePart(data: $data, where: $where) {
                id
                name
                description
            }
        }
    `;

    // The whole route in one call: the server validates the score weights
    // across all steps (sums need every row) and writes them transactionally,
    // so a failure can't leave a half-saved route.
    const SAVE_PART_ROUTE_MUTATION = gql`
        mutation SavePartRoute(
            $part_id: Int!
            $steps: [PartRouteStepInput!]!
            $deleted_part_stage_ids: [Int!]
        ) {
            savePartRoute(
                part_id: $part_id
                steps: $steps
                deleted_part_stage_ids: $deleted_part_stage_ids
            ) {
                part_id
                part_stage_ids
            }
        }
    `;

    const UPLOAD_ASSET_MUTATION = gql`
        mutation UploadAsset($file: Upload!, $semantic: Int!) {
            uploadAsset(file: $file, semantic: $semantic) {
                id
                url
            }
        }
    `;

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const patchSearchParams = (patch, options = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                next.delete(key);
            } else {
                next.set(key, value);
            }
        });
        setSearchParams(next, options);
    };

    const setShowInactive = (value, options = {}) =>
        patchSearchParams({ showInactive: value ? "1" : null }, options);

    const partQueryWhere = {
        plant_id: { equals: starekContext.lastUsedPlant.id },
        ...(showInactive ? {} : { is_active: { equals: true } }),
    };


    /**
     * Pulls the list again from the server after a write. `updateManyPart` and the
     * delete mutations only return a count, so nothing in the response tells Apollo
     * which Part rows changed — the list has to be re-read to pick up `is_active`.
     *
     * A failed refetch must not take the write down with it: the row is already saved,
     * and throwing here would surface as a bogus "delete failed".
     */
    // After any write, re-read the open record too (unless it was just deleted): the
    // page rows from before the refetch cannot tell whether it is still on the page, and
    // updateMany returns only a count, so the cached by-id copy would otherwise go stale.
    const hardRefresh = async () => {
        try {
            await refetch();
            // The open part may live on another page; re-read it so the sidebar sees the
            // write too. A just-deleted part is skipped — it would read as "not found".
            if (
                routePartId != null &&
                !deletedIdsRef.current.has(routePartId)
            ) {
                await refetchRoutePart();
            }
        } catch (err) {
            console.error("Failed to refresh parts:", err);
        }
    };

    const [deleteOnePart] = useMutation(DELETE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [deleteManyPart] = useMutation(DELETE_MANY_PARTS_MUTATION, {
        client: starekContext.engineClient,
    });
    const [createOnePart] = useMutation(CREATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [updateOnePart] = useMutation(UPDATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [uploadAsset] = useMutation(UPLOAD_ASSET_MUTATION, {
        client: starekContext.engineClient,
    });
    const [savePartRoute] = useMutation(SAVE_PART_ROUTE_MUTATION, {
        client: starekContext.engineClient,
    });
    const [updateManyPart] = useMutation(UPDATE_MANY_PART_MUTATION, { client: starekContext.engineClient });
    const [togglingActiveId, setTogglingActiveId] = useState(null);
    const [isTogglingActive, setIsTogglingActive] = useState(false);

    const [showAddPartDrawer, setShowAddPartDrawer] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [viewMode, setViewMode] = useState("card");
    const [routeEditPart, setRouteEditPart] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    // Tracks the route id we already widened the query for, so the retry runs at most once.
    // Ids just deleted: the refresh drops them while the route may still point at one.
    const deletedIdsRef = useRef(new Set());
    const [toggleCleared, setToggleCleared] = useState(false);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "card" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);

    // Table sort, applied on the server: { field, direction } or null for default order.
    const [sort, setSort] = useState(null);
    const orderBy = useMemo(() => sortToOrderBy(sort, ["description"]), [sort]);

    // One page of parts from the server. usePagedList is network-only on every
    // run: flipping "Show Inactive" swaps the variables, and cache-first would replay a
    // list captured before a part was deactivated or restored (CPRN00117-901).
    const {
        items,
        total,
        page,
        pageCount,
        pageSize,
        setPage,
        loading,
        initialLoading,
        refetch,
    } = usePagedList({
        query: GET_PARTS_LIST_FOR_PLANT,
        listField: "parts",
        countField: "aggregatePart",
        where: partQueryWhere,
        searchText: filterText,
        orderBy,
        client: starekContext.engineClient,
    });

    // Selection is per page: moving pages clears it.
    useEffect(() => {
        setSelectedRows([]);
        setToggleCleared((p) => !p);
    }, [page]);

    // /parts/:id may point at a part that is not on this page, so it is loaded on its
    // own (whatever its is_active; archived parts are excluded server-side).
    const {
        data: routePartData,
        loading: routePartLoading,
        refetch: refetchRoutePart,
    } = useQuery(GET_PART_BY_ID, {
        client: starekContext.engineClient,
        skip: routePartId == null,
        variables: {
            where: {
                id: { equals: routePartId },
                plant_id: { equals: starekContext.lastUsedPlant.id },
            },
        },
        fetchPolicy: "network-only",
    });

    // ?tab=route deep-links straight to a part's Route, which is where a freshly
    // created part has to go next. Anything else opens on Basic Details as before.
    const initialSidebarPage =
        searchParams.get("tab") === "route" ? "operations" : "basic_details";

    useEffect(() => {
        if (!routeId) {
            setRouteEditPart(null);
            return;
        }

        if (routePartLoading) return;

        const listRoute = buildListRoute("/parts", searchParams);

        if (routePartId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditPart(null);
            navigate(listRoute, { replace: true });
            return;
        }

        // Just deleted from the sidebar: the sidebar closes itself. Checked before the
        // lookup, because the by-id result can still hold the deleted row for a moment.
        if (deletedIdsRef.current.has(routePartId)) return;

        // Prefer the row on the current page (fresh after every refresh); otherwise the
        // part loaded by id. Either way inactive parts open without widening the list.
        const partToEdit =
            items.find((part) => Number(part?.id) === routePartId) ||
            routePartData?.findFirstPart;

        if (!partToEdit) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditPart(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditPart(partToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routePartId
                ? current
                : [partToEdit],
        );
        setShowAddPartDrawer(true);
    }, [
        routeId,
        routePartId,
        routePartLoading,
        routePartData,
        items,
        searchParams,
        navigate,
        starekContext,
    ]);

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    /**
     * Persists a part's operation sequence.
     *
     * Rows already in the database keep their id and get their sequence_no and override
     * refreshed; new rows are created; rows the user removed are deleted. sequence_no
     * arrives already renumbered contiguously from the sidebar.
     */
    const persistPartStages = async (
        partId,
        partOperations = [],
        deletedPartStageIds = [],
    ) => {
        await savePartRoute({
            variables: {
                part_id: Number(partId),
                // Order is the sequence; the server renumbers 1..n.
                steps: partOperations.map((row) => ({
                    id: row.id ? Number(row.id) : null,
                    operation_id: Number(row.operation_id),
                    // Ordered: first is the preferred machine. Empty means any capable one.
                    device_ids: row.device_ids || [],
                    standard_duration: row.standard_duration ?? null,
                    incremental_weight: Number(row.incremental_weight) || 0,
                    decremental_weight: Number(row.decremental_weight) || 0,
                    absolute_weight:
                        row.absolute_weight === null ||
                        row.absolute_weight === undefined ||
                        row.absolute_weight === ""
                            ? null
                            : Number(row.absolute_weight),
                })),
                deleted_part_stage_ids: deletedPartStageIds.map(Number),
            },
        });
    };

    const handleSavePart = async (partPayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id;

            if (!currentUserId) {
                throw new Error(UI_STRINGS.ERROR_NO_USER);
            }

            let assetId = null;
            if (partPayload.image_file) {
                const { data: assetData } = await uploadAsset({
                    variables: { file: partPayload.image_file, semantic: 0 },
                });
                assetId = assetData?.uploadAsset?.id;
            }

            const base = {
                name: partPayload.name,
                description: partPayload.description || "",
                plant: { connect: { id: Number(partPayload.plant_id) } },
                ...(assetId && {
                    image_asset: { connect: { id: assetId } },
                }),
            };

            // Handed back to the sidebar so its next step can deep-link this part.
            let savedPartId = partPayload.id ?? null;

            if (!partPayload.id) {
                // Create
                const data = {
                    ...base,
                    created_by: { connect: { id: Number(currentUserId) } },
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                const { data: created } = await createOnePart({
                    variables: { data },
                });
                const newPartId = created?.createOnePart?.id;
                savedPartId = newPartId ?? null;
                if (newPartId) {
                    await persistPartStages(
                        newPartId,
                        partPayload.partOperations,
                        [],
                    );
                }
            } else {
                // Update - only send fields that have values
                const data = {
                    updated_by: { connect: { id: Number(currentUserId) } },
                };

                if (partPayload.name) {
                    data.name = { set: partPayload.name };
                }

                if (partPayload.description !== undefined) {
                    data.description = { set: partPayload.description || "" };
                }

                if (assetId) {
                    data.image_asset = { connect: { id: assetId } };
                }

                await updateOnePart({
                    variables: { data, where: { id: partPayload.id } },
                });

                await persistPartStages(
                    partPayload.id,
                    partPayload.partOperations,
                    partPayload.deletedPartStageIds,
                );
            }

            // The sidebar stays open to confirm the save and point at the next screen;
            // it closes when the user picks a next step or presses Done.
            await hardRefresh();
            starekContext.showSuccess(
                partPayload.id
                    ? `Part "${partPayload.name}" updated successfully`
                    : `Part "${partPayload.name}" created successfully`,
            );
            return { id: savedPartId };
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/parts/${row.id}`, searchParams));
    };

    const handleAdd = () => {
        setRouteEditPart(null);
        setSelectedRows([]);
        setShowAddPartDrawer(true);
    };

    const handleEditSelected = () => {
        if (selectedRows.length !== 1) return;
        setRouteEditPart(null);
        setShowAddPartDrawer(true);
    };

    /**
     * Resolves only once the confirm dialog is answered — true when the parts were
     * deleted, false when the user backed out.
     *
     * The sidebar's Delete button awaits this. Before, it got back `undefined`, so
     * `SidebarContextProvider` treated the delete as finished the moment the dialog
     * opened and tore the panel down on top of it, leaving the confirm unusable.
     */
    const handleDelete = (parts) =>
        new Promise((resolve) => {
            if (!parts || parts.length === 0 || isDeleting) {
                resolve(false);
                return;
            }

            const isSingle = parts.length === 1;
            const title = isSingle
                ? UI_STRINGS.DELETE_TITLE_SINGLE(parts[0].name)
                : UI_STRINGS.DELETE_TITLE_BULK(parts.length);

            const message = isSingle
                ? UI_STRINGS.DELETE_MESSAGE_SINGLE(parts[0].name)
                : UI_STRINGS.DELETE_MESSAGE_BULK(parts.length);

            openConfirm({
                title: title,
                content: (
                    <>
                        <div
                            className="text-dark"
                            style={{ whiteSpace: "pre-wrap" }}
                        >
                            {message}
                        </div>
                        <p className="mt-4 text-dark fw-bold">
                            {UI_STRINGS.TOTAL_SELECTED(parts.length)}
                        </p>
                    </>
                ),
                confirmText: UI_STRINGS.DELETE_CONFIRM,
                cancelText: UI_STRINGS.DELETE_CANCEL,
                type: "danger",
                onConfirm: async () => {
                    setIsDeleting(true);
                    setDeleteDone(0);

                    const ids = parts.map((p) => p.id);

                    try {
                        // Try batch delete first
                        await deleteManyPart({ variables: { ids } });
                        setDeleteDone(ids.length);
                        starekContext.showSuccess(
                            `Successfully deleted ${ids.length} part(s)`,
                        );
                    } catch {
                        // Fallback to individual deletes
                        for (const id of ids) {
                            try {
                                await deleteOnePart({
                                    variables: { where: { id } },
                                });
                                setDeleteDone((d) => d + 1);
                            } catch (err) {
                                starekContext.showError?.(err);
                                break;
                            }
                        }
                    } finally {
                        ids.forEach((id) => deletedIdsRef.current.add(Number(id)));
                        setIsDeleting(false);
                        setSelectedRows([]);
                        setToggleCleared((p) => !p);
                        await hardRefresh();
                        closeConfirm();
                        resolve(true);
                    }
                },
                onClose: () => {
                    closeConfirm();
                    resolve(false);
                },
            });
        });

    const handleDrawerClose = async (itemModified, itemDeleted) => {
        setShowAddPartDrawer(false);
        setRouteEditPart(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        if (routeId) {
            // ?tab belongs to the part being closed, not to the list behind it.
            const listParams = new URLSearchParams(searchParams);
            listParams.delete("tab");
            navigate(buildListRoute("/parts", listParams), {
                replace: true,
            });
        }

        await new Promise((resolve) => setTimeout(resolve, 0));
        await hardRefresh();
    };

    /**
     * Deactivating is warned about but never blocked — a part already on a job schedule
     * keeps running. Restoring needs no warning, so it skips the dialog.
     */
    const confirmDeactivate = (parts, proceed) => {
        const isSingle = parts.length === 1;
        openConfirm({
            title: isSingle
                ? UI_STRINGS.DEACTIVATE_TITLE_SINGLE(parts[0].name)
                : UI_STRINGS.DEACTIVATE_TITLE_BULK(parts.length),
            content: (
                <div className="text-dark" style={{ whiteSpace: "pre-wrap" }}>
                    {UI_STRINGS.DEACTIVATE_WARNING}
                </div>
            ),
            confirmText: UI_STRINGS.DEACTIVATE_CONFIRM,
            cancelText: UI_STRINGS.DEACTIVATE_CANCEL,
            type: "warning",
            onConfirm: async () => {
                await proceed();
                closeConfirm();
            },
            onClose: closeConfirm,
        });
    };

    const applyToggleActive = async (parts, makeActive) => {
        const ids = parts.map((p) => p.id);
        setIsTogglingActive(true);
        try {
            await updateManyPart({
                variables: {
                    data: makeActive ? restoreData() : deactivateData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(
                makeActive
                    ? `Restored ${ids.length} part(s)`
                    : `Deactivated ${ids.length} part(s)`,
            );
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await hardRefresh();
        } catch (err) {
            starekContext.showError(err);
        } finally {
            setIsTogglingActive(false);
        }
    };

    const handleToggleActive = async (parts, makeActive) => {
        if (!parts || parts.length === 0) return;
        if (makeActive) {
            await applyToggleActive(parts, true);
            return;
        }
        confirmDeactivate(parts, () => applyToggleActive(parts, false));
    };

    const handleToggleActiveSingle = async (part) => {
        const deactivating = part.is_active;

        const apply = async () => {
            setTogglingActiveId(part.id);
            setIsTogglingActive(true);
            try {
                await updateManyPart({
                    variables: {
                        data: deactivating ? deactivateData() : restoreData(),
                        where: { id: { in: [part.id] } },
                    },
                });
                starekContext.showSuccess(
                    deactivating
                        ? UI_STRINGS.DEACTIVATED(part.name)
                        : UI_STRINGS.RESTORED(part.name),
                );
                await hardRefresh();
            } catch (err) {
                starekContext.showError(err);
            } finally {
                setTogglingActiveId(null);
                setIsTogglingActive(false);
            }
        };

        if (!deactivating) {
            await apply();
            return;
        }
        confirmDeactivate([part], apply);
    };

    const partExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    // Only the first load gets the full-page spinner. Later loads — the "Show Inactive"
    // toggle swapping variables, a refetch after a write — keep the list on screen, so
    // an open sidebar or confirm dialog is not torn down mid-action.
    if (initialLoading) return <EntityLoadingState icon="fa-cube" />;

    const activePart = routeEditPart || selectedRows[0] || null;

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={partExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); }}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    patchSearchParams({ view: mode });
                }}
                onDelete={() => handleDelete(selectedRows)}
                onEdit={handleEditSelected}
            >
                <EntityOperationBar.View
                    mode="card"
                    icon="fa-th"
                    tooltip={UI_STRINGS.VIEW_MODE_CARD}
                >
                    <PartsCardView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        onAdd={handleAdd}
                        onRowClicked={handleRowClicked}
                        onToggleActive={canWrite ? handleToggleActiveSingle : undefined}
                        togglingActiveId={togglingActiveId}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list-ul"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <PartsListingView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        toggleCleared={toggleCleared}
                        noDataComponent={<EntityEmptyState icon="fa-cube" entityName="Part" messages={EMPTY_MESSAGES.parts} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        onRowClicked={handleRowClicked}
                        onSort={setSort}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <Paginator
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                total={total}
                onChange={setPage}
                disabled={loading}
            />

            {showAddPartDrawer && (
                <PartSidebar
                    show={true}
                    item={activePart}
                    plantId={starekContext.lastUsedPlant.id}
                    onDiscard={handleDrawerClose}
                    onSave={handleSavePart}
                    onOpenSaved={(saved) =>
                        navigate(buildListRoute(`/parts/${saved.id}`, searchParams))
                    }
                    onDelete={(part) => handleDelete([part])}
                    onToggleActive={canWrite ? handleToggleActiveSingle : undefined}
                    initialPage={initialSidebarPage}
                />
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting || isTogglingActive}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { Parts };
